import React, { useState } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AlertTriangle } from 'lucide-react';
import { inr, pct } from './format';

const WEAK_COUNT = 3;
const anyNeg = (r) => [r.inward_margin_pct, r.outward_margin_pct, r.current_margin_pct].some((v) => v !== null && v < 0);
const isWeak = (r, idx, total) => anyNeg(r) || (total >= WEAK_COUNT * 2 && idx < WEAK_COUNT && r.current_margin_pct !== null);

export const MarginBreakdown = ({ byCategory = [], byBrand = [], onPick }) => {
  const [dim, setDim] = useState('category');
  const data = dim === 'category' ? byCategory : byBrand;
  const chartData = data.filter((d) => d.current_margin_pct !== null || d.inward_margin_pct !== null).slice(0, 12);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="margin-breakdown">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-slate-800">Margin by {dim === 'category' ? 'Category' : 'Brand'}</div>
          <div className="text-[11px] text-slate-500">Inward (GRN price vs MRP), Outward (selling price vs MRP) and Current (selling vs GRN price) — negative margins & weakest current margins highlighted · chart axis capped at ±100% · click to filter</div>
        </div>
        <div className="flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
          {['category', 'brand'].map((d) => (
            <button key={d} type="button" onClick={() => setDim(d)} data-testid={`margin-dim-${d}`}
              className={`px-3 py-1 text-xs rounded-md capitalize transition-colors ${dim === d ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'}`}>{d}</button>
          ))}
        </div>
      </div>
      <div className="h-64" data-testid="margin-breakdown-chart">
        <ResponsiveContainer>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="name" fontSize={11} tickFormatter={(n) => (n.length > 14 ? `${n.slice(0, 13)}…` : n)} />
            <YAxis fontSize={11} unit="%" domain={[-100, 100]} allowDataOverflow />
            <ReferenceLine y={0} stroke="#94a3b8" />
            <Tooltip formatter={(v) => pct(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="inward_margin_pct" onClick={(d) => onPick(dim, d?.payload?.name)} name="Inward Margin %" fill="#0ea5e9" radius={[3, 3, 0, 0]} cursor="pointer" />
            <Bar dataKey="outward_margin_pct" onClick={(d) => onPick(dim, d?.payload?.name)} name="Outward Margin %" fill="#8b5cf6" radius={[3, 3, 0, 0]} cursor="pointer" />
            <Bar dataKey="current_margin_pct" onClick={(d) => onPick(dim, d?.payload?.name)} name="Current Margin %" fill="#10b981" radius={[3, 3, 0, 0]} cursor="pointer" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="overflow-auto max-h-72 rounded-lg border border-slate-100">
        <table className="w-full text-xs" data-testid="margin-breakdown-table">
          <thead className="sticky top-0 bg-slate-900 text-slate-100">
            <tr>{[dim === 'category' ? 'Category' : 'Brand', 'GRN Lines', 'GRN Value', 'Inward %', 'Inward Value', 'Outward %', 'Outward Value', 'Current %', 'Current Value'].map((h) => (
              <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>))}</tr>
          </thead>
          <tbody>
            {data.map((r, i) => {
              const weak = isWeak(r, i, data.length);
              const neg = (v) => (v < 0 ? 'text-rose-600 font-semibold' : '');
              return (
                <tr key={r.name} onClick={() => onPick(dim, r.name)} data-testid="margin-breakdown-row"
                  className={`border-t border-slate-100 cursor-pointer transition-colors ${weak ? 'bg-rose-50 hover:bg-rose-100' : 'hover:bg-indigo-50/40'}`}>
                  <td className="px-3 py-2 font-medium text-slate-800 whitespace-nowrap">
                    {weak && <AlertTriangle size={12} className="inline mr-1 text-rose-600" data-testid="margin-weak-flag" />}{r.name}
                  </td>
                  <td className="px-3 py-2 tabular-nums">{r.lines}</td>
                  <td className="px-3 py-2 tabular-nums">{inr(r.grn_value)}</td>
                  <td className={`px-3 py-2 tabular-nums ${neg(r.inward_margin_pct)}`}>{pct(r.inward_margin_pct)}</td>
                  <td className={`px-3 py-2 tabular-nums ${neg(r.inward_margin_value)}`}>{inr(r.inward_margin_value)}</td>
                  <td className={`px-3 py-2 tabular-nums ${neg(r.outward_margin_pct)}`}>{pct(r.outward_margin_pct)}</td>
                  <td className={`px-3 py-2 tabular-nums ${neg(r.outward_margin_value)}`}>{inr(r.outward_margin_value)}</td>
                  <td className={`px-3 py-2 tabular-nums ${neg(r.current_margin_pct)}`}>{pct(r.current_margin_pct)}</td>
                  <td className={`px-3 py-2 tabular-nums ${neg(r.current_margin_value)}`}>{inr(r.current_margin_value)}</td>
                </tr>
              );
            })}
            {!data.length && <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-500">No GRN lines for the selected filters</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
